import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Mail, Loader2, CheckCircle2, XCircle } from "lucide-react";
import DOMPurify from "dompurify";
import EmailComposeDialog from "@/components/EmailComposeDialog";

const TEMPLATE_LABELS = {
  cash_offer: "Cash Offer",
  follow_up: "Follow-up",
  contract_executed: "Contract",
  cold_outreach: "Cold Outreach",
};

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function Communications() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [compose, setCompose] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setBusy(true);
    api
      .get("/emails")
      .then((r) => setItems(r.data.items || []))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div
      className="p-6 md:p-10 max-w-[1400px]"
      data-testid="communications-page"
    >
      <div className="flex items-end justify-between mb-6 reveal">
        <div>
          <span className="app-eyebrow">/ Inbox log</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3">
            Communications
          </h1>
          <p className="text-[var(--app-text-muted)] mt-2 max-w-lg">
            Every email sent from FlipPilot. Replies from sellers go directly to
            your login email inbox.
          </p>
        </div>
        <button
          onClick={() => setCompose(true)}
          data-testid="new-email-button"
          className="app-btn app-btn-primary"
        >
          <Mail size={14} /> New email
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div
          className="card-flat lg:col-span-2 overflow-hidden reveal reveal-2"
          data-testid="email-list"
        >
          {busy && (
            <div className="p-10 text-center text-[var(--app-text-muted)]">
              <Loader2 className="animate-spin inline" size={16} /> Loading…
            </div>
          )}
          {!busy && items.length === 0 && (
            <div className="p-10 text-center text-[var(--app-text-muted)]">
              No emails yet. Send your first from any lead or property.
            </div>
          )}
          {!busy &&
            items.map((c) => (
              <button
                key={c.id}
                data-testid={`email-item-${c.id}`}
                onClick={() => setSelected(c)}
                className={`w-full text-left px-5 py-4 border-b border-[var(--app-line)] hover:bg-[var(--app-surface)] transition-colors ${
                  selected?.id === c.id ? "bg-[var(--app-surface)]" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {c.status === "sent" ? (
                        <CheckCircle2
                          size={13}
                          className="text-emerald-600 shrink-0"
                        />
                      ) : (
                        <XCircle size={13} className="text-red-600 shrink-0" />
                      )}
                      <span className="text-sm font-medium truncate">
                        {c.recipient_email}
                      </span>
                      {c.template_key && (
                        <span className="text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-[var(--app-surface-2)] text-[var(--app-accent-dark)]">
                          {TEMPLATE_LABELS[c.template_key] || c.template_key}
                        </span>
                      )}
                    </div>
                    <div className="text-sm truncate mt-1">{c.subject}</div>
                  </div>
                  <div className="text-xs text-[var(--app-text-muted)] shrink-0">
                    {timeAgo(c.created_at)}
                  </div>
                </div>
              </button>
            ))}
        </div>

        <div className="card-flat reveal reveal-3" data-testid="email-preview">
          {!selected && (
            <div className="p-10 text-center text-sm text-[var(--app-text-muted)]">
              Select an email to preview.
            </div>
          )}
          {selected && (
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--app-text-muted)]">
                To
              </div>
              <div className="font-medium">{selected.recipient_email}</div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[var(--app-text-muted)]">
                Subject
              </div>
              <div className="font-medium">{selected.subject}</div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[var(--app-text-muted)]">
                Body
              </div>
              <div
                className="mt-1 text-sm prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selected.html_body || "") }}
              />
              {selected.error && (
                <div className="mt-4 p-3 bg-red-50 text-red-700 text-xs rounded-xl">
                  {selected.error}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <EmailComposeDialog
        open={compose}
        onOpenChange={setCompose}
        onSent={() => load()}
      />
    </div>
  );
}
